import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QUIZMASTER_API } from '../config';
import '../styles/styles.css';
import FloatingChat from './FloatingChat'; 

const QuizPage = ({ accessToken, supabaseRefreshToken, onSignIn }) => {
    const [quizQuestions, setQuizQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userResponses, setUserResponses] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [timer, setTimer] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();
    const instanceId = location.state?.selectedInstanceId;
    const document = location.state?.selectedDocument;
    const handleReturnToQuizzes = useCallback(() => {
        if (document) {
            navigate('/quiz-list', { state: { selectedDocument: document } });
        } else {
            navigate('/profile');
        }
    }, [document, navigate]);

    const refreshToken = useCallback(async () => {
        try {
            const refreshResponse = await fetch(`${QUIZMASTER_API}/auth/refresh`, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: supabaseRefreshToken }),
            });
            if (!refreshResponse.ok) throw new Error('Token refresh failed');
            const { access_token: newAccessToken, refresh_token: newRefreshToken } = await refreshResponse.json();
            onSignIn({ accessToken: newAccessToken, refreshToken: newRefreshToken });
            return newAccessToken;
        } catch (err) {
            throw new Error('Session expired. Please sign in again.');
        }
    }, [supabaseRefreshToken, onSignIn]);

    const fetchWithAuth = useCallback(async (url, options = {}) => {
        let response = await fetch(url, {
            ...options,
            headers: { ...options.headers, 'Authorization': `Bearer ${accessToken}`, 'Accept': 'application/json' },
        });
        if (response.status === 401 && supabaseRefreshToken) {
            const newAccessToken = await refreshToken();
            response = await fetch(url, {
                ...options,
                headers: { ...options.headers, 'Authorization': `Bearer ${newAccessToken}`, 'Accept': 'application/json' },
            });
        }
        return response;
    }, [accessToken, supabaseRefreshToken, refreshToken]);

    useEffect(() => {
        if (!instanceId) {
            setError('No quiz instance selected.');
            setLoading(false);
            return;
        }

        const fetchQuizInstanceDetails = async () => {
            try {
                const response = await fetchWithAuth(
                    `${QUIZMASTER_API}/get-quiz-instance-details/?instance_id=${instanceId}`
                );
                if (!response.ok) throw new Error('Failed to fetch quiz instance details');
                const data = await response.json();
                const formattedQuestions = data.questions.map((question) => ({
                    ...question,
                    submitted_response_option_id: question.submitted_response_option_id || null,
                }));
                setQuizQuestions(formattedQuestions);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchQuizInstanceDetails();
    }, [instanceId, fetchWithAuth]);

    useEffect(() => {
        let interval;
        if (!loading && !submitting) {
            interval = setInterval(() => {
                setTimer((prev) => prev + 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [loading, submitting]);

    const handleResponseSelection = (questionId, responseId) => {
        setUserResponses((prev) => ({ ...prev, [questionId]: responseId }));
    };

    const submitResponse = async (questionId, responseId) => {
        if (!responseId) return;
        setSubmitting(true);
        try {
            const response = await fetchWithAuth(
                `${QUIZMASTER_API}/submit-quiz-instance-responses/?instance_id=${instanceId}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify([{ question_id: questionId, response_option_id: responseId, time_taken_to_respond_sec: timer }]),
                }
            );
            if (!response.ok) throw new Error('Failed to submit quiz instance response');
            await response.json();
            setQuizQuestions((prev) =>
                prev.map((q) => (q.question_id === questionId ? { ...q, submitted_response_option_id: responseId } : q))
            );
            setTimer(0);
        } catch (err) {
            console.error(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handlePreviousQuestion = async () => {
        const currentQuestion = quizQuestions[currentQuestionIndex];
        const selectedResponse = userResponses[currentQuestion.question_id];
        if (selectedResponse && !currentQuestion.submitted_response_option_id) {
            await submitResponse(currentQuestion.question_id, selectedResponse);
        }
        setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
        setTimer(0);
    };

    const handleNextQuestion = async () => {
        const currentQuestion = quizQuestions[currentQuestionIndex];
        const selectedResponse = userResponses[currentQuestion.question_id];
        if (selectedResponse && !currentQuestion.submitted_response_option_id) {
            await submitResponse(currentQuestion.question_id, selectedResponse);
        }
        setCurrentQuestionIndex((prev) => Math.min(prev + 1, quizQuestions.length - 1));
        setTimer(0);
    };

    const updateQuizInstanceStatus = async (status) => {
        try {
            const response = await fetchWithAuth(
                `${QUIZMASTER_API}/update-quiz-instance-status/?instance_id=${instanceId}&status=${status}`,
                { method: 'PUT' }
            );
            if (!response.ok) throw new Error('Failed to update quiz instance status');
            return await response.json();
        } catch (err) {
            console.error(err.message);
            throw err;
        }
    };

    const handleSubmitQuiz = async () => {
        const currentQuestion = quizQuestions[currentQuestionIndex];
        const selectedResponse = userResponses[currentQuestion.question_id];
        if (selectedResponse && !currentQuestion.submitted_response_option_id) {
            await submitResponse(currentQuestion.question_id, selectedResponse);
        }
        setSubmitting(true);
        try {
              await updateQuizInstanceStatus('Completed');
              navigate('/results', {
                  state: document
                      ? {
                          selectedInstanceId: instanceId,
                          selectedDocument: document,
                      }
                      : {
                          selectedInstanceId: instanceId,
                      },
              });
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
            setTimer(0);
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    if (error || !instanceId) {
          return (
              <div className="error-container">
                  <p>{error || 'No quiz instance selected.'}</p>
                  <button className="button confirm-button" onClick={handleReturnToQuizzes}>
                      Back to Quizzes
                  </button>
              </div>
          );
    }

    if (quizQuestions.length === 0) {
          return (
              <div className="container">
                  <h2 className="sub-header">No questions found for this quiz instance.</h2>
                  <button className="button confirm-button" onClick={handleReturnToQuizzes}>
                      Back to Quizzes
                  </button>
              </div>
          );
    }

    const currentQuestion = quizQuestions[currentQuestionIndex];
    const progressPercentage = ((currentQuestionIndex + 1) / quizQuestions.length) * 100;

    const totalQuestions = quizQuestions.length;
    const questionNumber = currentQuestionIndex + 1;
    const isQuestionSubmitted = currentQuestion.submitted_response_option_id !== null;
    const pendingSelectionId = userResponses[currentQuestion.question_id];
    const activeSelectionId = isQuestionSubmitted
        ? currentQuestion.submitted_response_option_id
        : pendingSelectionId;

    return (
        <div className="scroll-container quiz-page">
            <div className="quiz-page__header">
                <div className="quiz-page__titles">
                    <h1 className="quiz-page__title">{document?.filename ? `${document.filename} Quiz` : 'Quiz In Progress'}</h1>
                    <p className="quiz-page__subtitle">Stay focused and work through each question at your own pace.</p>
                </div>
                <div className="quiz-page__meta-group">
                    <div className="quiz-status-pill" aria-live="polite">
                        <span className="quiz-status-pill__label">Question</span>
                        <span className="quiz-status-pill__value">{questionNumber} / {totalQuestions}</span>
                    </div>
                    <div className="quiz-status-pill" aria-live="polite">
                        <span className="quiz-status-pill__label">On this question</span>
                        <span className="quiz-status-pill__value">{timer} sec</span>
                    </div>
                    {instanceId && (
                        <div className="quiz-status-pill quiz-status-pill--muted" title="Quiz instance identifier">
                            <span className="quiz-status-pill__label">Instance</span>
                            <span className="quiz-status-pill__value quiz-status-pill__value--truncate">{instanceId}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="quiz-progress" aria-hidden="true">
                <div className="quiz-progress__bar">
                    <div className="quiz-progress__fill" style={{ width: `${progressPercentage}%` }}></div>
                </div>
            </div>

            <article className="quiz-question-card" aria-labelledby="quiz-question-heading">
                <header className="quiz-question-card__header">
                    <p className="quiz-question-card__eyebrow">Question {questionNumber}</p>
                    <h2 id="quiz-question-heading" className="quiz-question-card__title">{currentQuestion.question_text}</h2>
                </header>

                <ul className="quiz-options" role="radiogroup" aria-label="Answer choices">
                    {currentQuestion.response_options.map((option, optionIndex) => {
                        const optionLetter = String.fromCharCode(65 + optionIndex);
                        const isPersistedSelection = currentQuestion.submitted_response_option_id === option.id;
                        const isPendingSelection = pendingSelectionId === option.id;
                        const isSelected = activeSelectionId === option.id;
                        const isCorrect = Boolean(option.is_correct);

                        const optionClasses = [
                            'quiz-option',
                            isSelected ? 'quiz-option--selected' : '',
                            isQuestionSubmitted ? 'quiz-option--locked' : '',
                            isQuestionSubmitted && isCorrect ? 'quiz-option--correct' : '',
                            isQuestionSubmitted && isPersistedSelection && !isCorrect ? 'quiz-option--incorrect' : '',
                        ]
                            .filter(Boolean)
                            .join(' ');

                        return (
                            <li key={option.id} className="quiz-options__item">
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={isSelected}
                                    className={optionClasses}
                                    onClick={() => handleResponseSelection(currentQuestion.question_id, option.id)}
                                    disabled={isQuestionSubmitted}
                                >
                                    <span className="quiz-option__letter" aria-hidden="true">{optionLetter}</span>
                                    <span className="quiz-option__body">
                                        <span className="quiz-option__text">{option.response_text}</span>
                                        {!isQuestionSubmitted && isPendingSelection && (
                                            <span className="quiz-option__meta">Selected</span>
                                        )}
                                        {isQuestionSubmitted && isCorrect && (
                                            <span className="quiz-option__meta quiz-option__meta--success">Correct answer</span>
                                        )}
                                        {isQuestionSubmitted && isPersistedSelection && !isCorrect && (
                                            <span className="quiz-option__meta quiz-option__meta--error">Your choice</span>
                                        )}
                                    </span>
                                    {isQuestionSubmitted && (
                                        <span className="quiz-option__icon" aria-hidden="true">
                                            {isCorrect ? '✔' : isPersistedSelection ? '✘' : ''}
                                        </span>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>

                <footer className="quiz-question-card__footer" role="status" aria-live="polite">
                    {isQuestionSubmitted ? (
                        currentQuestion.submitted_response_option_id === null ? (
                            <span className="quiz-question-card__helper quiz-question-card__helper--muted">No response recorded for this question.</span>
                        ) : (
                            <span
                                className={`quiz-question-card__helper ${
                                    currentQuestion.response_options.find((option) => option.id === currentQuestion.submitted_response_option_id)?.is_correct
                                        ? 'quiz-question-card__helper--success'
                                        : 'quiz-question-card__helper--error'
                                }`}
                            >
                                {currentQuestion.response_options.find((option) => option.id === currentQuestion.submitted_response_option_id)?.is_correct
                                    ? 'Well done — you answered correctly.'
                                    : 'Incorrect. Review the explanation in your results later.'}
                            </span>
                        )
                    ) : (
                        <span className="quiz-question-card__helper quiz-question-card__helper--muted">
                            {activeSelectionId ? 'Double-check your choice before moving on.' : 'Select the best answer to continue.'}
                        </span>
                    )}
                </footer>
            </article>

            <div className="quiz-page__actions">
                  <button
                      type="button"
                      className="button button--ghost quiz-page__back"
                      onClick={handleReturnToQuizzes}
                  >
                    Back to Quizzes
                </button>
                <div className="quiz-page__nav">
                    <button
                        type="button"
                        className="button button--secondary"
                        onClick={handlePreviousQuestion}
                        disabled={currentQuestionIndex === 0}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        className="button"
                        onClick={questionNumber === totalQuestions ? handleSubmitQuiz : handleNextQuestion}
                        disabled={submitting}
                    >
                        {questionNumber === totalQuestions ? (submitting ? 'Submitting…' : 'Submit Quiz') : (submitting ? 'Submitting…' : 'Next Question')}
                    </button>
                </div>
            </div>
            {document && (
                <FloatingChat
                    documentId={document.id}
                    filename={document.filename}
                />
            )}
        </div>
    );
};

export default QuizPage;